import type { Router } from 'vue-router'
import { getActivePinia } from 'pinia'
import { noop as _noop } from 'lodash-es'
import { type GtmSupport, type TrackEventOptions, createGtm, useGtm } from '@gtm-support/vue-gtm'

import { GtmEventMap } from '@/view-models/gtm'

export default defineNuxtPlugin(nuxtApp => {
  const config = useRuntimeConfig()

  const isProd = config.public.configEnv === 'prod'
  const gtmId = config.public.GTM_ID
  const fallbackGtm = { dataLayer: _noop, trackView: _noop, trackEvent: _noop, push: _noop } as GtmSupport

  let gtm: GtmSupport | undefined
  let trackEvent: (data: string | TrackEventOptions) => void = () => {}

  if (gtmId) {
    nuxtApp.vueApp.use(
      createGtm({
        id: gtmId,
        defer: true,
        compatibility: false,
        enabled: true,
        debug: !isProd,
        loadScript: true,
        vueRouter: useRouter() as Router,
        trackOnNextTick: false,
      })
    )
    gtm = useGtm()
    trackEvent = data => {
      const store = getActivePinia()
      const eventPayload = typeof data === 'string' ? { event: data } : data
      gtm?.trackEvent({ store: null, payload: null })
      gtm?.trackEvent({ ...eventPayload, store: store?.state.value })
    }
  }

  // setup gtm directive
  nuxtApp.vueApp.directive('gtm', {
    mounted(el, binding) {
      const { arg: domEvent, value: gtmEvent, modifiers } = binding
      const domEventOptions = {
        once: modifiers.once ?? false,
        passive: modifiers.passive,
        capture: modifiers.capture,
      }
      if (domEvent) {
        el.addEventListener(
          domEvent,
          () => {
            trackEvent(gtmEvent)
          },
          domEventOptions
        )
      } else {
        trackEvent(gtmEvent)
      }
    },
  })

  if (import.meta.client) {
    // setup gtm dataLayer
    window.dataLayer ||= []

    // expose GtmEventMap to window for GTM tag debugging
    window.FnGtmEvt = GtmEventMap
  }

  return {
    provide: {
      gtm: gtm || fallbackGtm,
      trackEvent,
    },
  }
})
